import { Component, Inject, OnDestroy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { convertEpochToDateString } from '@functions/functions';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { AccountFeatures, AccountsService, IAccount } from '@services/accounts.service';
import { Action, ActionsDataService, IActionType } from '@services/actions-data.service';
import { AssetHistory, AssetsDataService, LevelAsset } from '@services/assets-data.service';
import { CaseFile } from '@services/cmms.service';
import { DeviationEvent, OfflineEvent, RuleEvent } from '@services/events-data.service';
import { ModelsService, ModelType } from '@services/models.service';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { combineLatest, distinctUntilChanged, filter, finalize, Subject, take } from 'rxjs';

/**
 * ActionTypeFilter
 */
interface ActionTypeFilter {
	value: number;
	description: string;
}

/**
 * AssetHistoryComponent
 */
@Component({
	selector: 'app-asset-history',
	templateUrl: './asset-history.component.html',
	styleUrls: ['./asset-history.component.scss']
})
export class AssetHistoryComponent implements OnDestroy {
	/** Form Group */
	dialogForm: FormGroup;

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** accountGUID */
	public accountGUID: string;

	/** organisationUID */
	public organisationUID: string;

	/** assetID */
	public asset: LevelAsset;

	/** assetHistory */
	public assetHistory: AssetHistory;

	public ActionHistory: Action[];

	/** fields */
	public fields: { text: string, value: string } = { text: "RuleText", value: "RuleEventID" };

	/** fields */
	public fields2: { text: string, value: string } = { text: "SensorName", value: "OfflineEventID" };

	/** fields */
	public fields3: { text: string, value: string } = { text: "ActionDetails", value: "ActionID" };

	/** fields */
	public fields4: { text: string, value: string } = { text: "Message", value: "DeviationEventID" };

	/** CMMS Case Fields */
	public fields5: { text: string, value: string } = { text: "Description", value: "Id" };

	/** actionTypes - values correspond to value in db table */
	public actionTypes: IActionType[] = [];

	/** actionTypesFilter */
	public actionTypesFilter: ActionTypeFilter[] = [];

	public actionTypesFilterID: number = 0;

	public actionTypesFields = {
		text: 'description',
		value: 'value'
	};

	/** numberOfColumns */
	public numberOfColumns: number;

	/** thirtyDaysAgo */
	public thirtyDaysAgo: number;

	/** assetHistoryThirtyDays */
	public assetHistoryThirtyDays: AssetHistory;

	/** selectedTab */
	public selectedTab: number = 0;

	/** modelTypes */
	public modelTypes: ModelType[] = [];

	/** selectedAccount */
	public selectedAccount: IAccount;

	/** AccountFeatures */
	public AccountFeatures = AccountFeatures

	/**
	 * Default Constructor
	 * @param dialogRef dialogRef 
	 * @param dialogData dialogData 
	 * @param globalFunctions globalFunctions 
	 * @param assetsDataService assetsDataService 
	 * @param modelsService modelsService
	 * @param authenticationService authenticationService
	 * @param router router
	 * @param siteSubscriptions siteSubscriptions
	 * @param actionsDataService ActionsDataService
	 */
	constructor(
		public dialogRef: MatDialogRef<AssetHistoryComponent>,
		@Inject(MAT_DIALOG_DATA) public dialogData: any,
		private globalFunctions: GlobalFunctions,
		private assetsDataService: AssetsDataService,
		private modelsService: ModelsService,
		private router: Router,
		private siteSubscriptions: siteSubscriptions,
		private actionsDataService: ActionsDataService
	) {
		this.accountGUID = dialogData.accountGUID ?? "";
		this.organisationUID = dialogData.organisationUID ?? "";
		this.asset = dialogData.asset ?? 0;
	}

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {
		this.getAssetHistory(this.asset.AssetID);
		this.thirtyDaysAgo = (Date.now() - (30 * 24 * 60 * 60 * 1000));
		// this.globalFunctions.displaySpinner(true);
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * Cancel button for dialog
	 */
	onCancelClick(): void {
		this.dialogRef.close();
	}

	/**
	 * getAssetHistory
	 * @param assetID assetID 
	 */
	getAssetHistory(assetID: number) {

		combineLatest([
			this.siteSubscriptions.selectedAccount$.pipe(distinctUntilChanged()),
			this.actionsDataService.getActionTypes$().pipe(distinctUntilChanged()),
			this.assetsDataService.getAssetHistory$(this.accountGUID, this.organisationUID, assetID).pipe(distinctUntilChanged()),
			this.modelsService.getModelTypes$(this.accountGUID, this.organisationUID).pipe(distinctUntilChanged())
		]).pipe(
			take(1),
			filter(([selectedAccount, ActionTypes, history, modelTypes]) => !!selectedAccount && !!ActionTypes && !!history && !!modelTypes),
			finalize(() => { this.globalFunctions.displaySpinner(false); })
		).subscribe({
			next: ([selectedAccount, ActionTypes, history, modelTypes]) => {
				this.numberOfColumns = 0;
				let ruleEvents: RuleEvent[] = history.RuleEventHistory != '' ? JSON.parse(history.RuleEventHistory) : [];
				let sensorEvents: OfflineEvent[] = history.SensorEventHistory != '' ? JSON.parse(history.SensorEventHistory) : [];
				this.ActionHistory = history.ActionHistory != '' ? JSON.parse(history.ActionHistory) : [];
				let deviationEvents: DeviationEvent[] = history.DeviationEventHistory != '' ? JSON.parse(history.DeviationEventHistory) : [];
				let CMMSCaseHistory: CaseFile[] = history.CMMSCaseHistory != '' ? JSON.parse(history.CMMSCaseHistory) : [];

				this.selectedAccount = selectedAccount;
				this.actionTypes = ActionTypes;
				ruleEvents = ruleEvents.filter((r: RuleEvent) => !r.Archived); //issue #92
				if (ruleEvents.length > 0) {
					this.numberOfColumns++;
				}
				if (sensorEvents.length > 0) {
					this.numberOfColumns++;
				}
				if (this.ActionHistory.length > 0) {
					// this.actionTypesFilter = [
					// 	...new Map(
					// 		this.ActionHistory.map(action => [
					// 			action.ActionType,
					// 			this.getActionTypeFromValue(action.ActionType)
					// 		])
					// 	)
					// ].sort((a, b) => a[1].localeCompare(b[1]));

					// this.actionTypesFilter.unshift([0, "All"]);
					this.actionTypesFilter = this.ActionHistory
						.map(action => ({
							value: action.ActionType,
							description: this.getActionTypeFromValue(action.ActionType)
						}))
						.filter((item, index, array) =>
							array.findIndex(x => x.value === item.value) === index
						)
						.sort((a, b) => a.description.localeCompare(b.description));

					this.actionTypesFilter.unshift({
						value: 0,
						description: 'All'
					});

					this.numberOfColumns++;
				}
				if (deviationEvents.length > 0) {
					this.numberOfColumns++;
					this.modelTypes = modelTypes;
					// this.getModelTypes();
				}
				if (CMMSCaseHistory.length > 0) {
					this.numberOfColumns++;
				}

				let ruleEventsThirtyDays: RuleEvent[] = ruleEvents.filter((x: RuleEvent) => x.TimeStamp > this.thirtyDaysAgo);
				let sensorEventsThirtyDays: OfflineEvent[] = sensorEvents.filter((x: OfflineEvent) => x.TimeStamp > this.thirtyDaysAgo);
				let actionsThirtyDays: Action[] = this.ActionHistory.filter((x: Action) => x.Timestamp > this.thirtyDaysAgo);
				let deviationEventsThirtyDays: DeviationEvent[] = deviationEvents.filter((x: DeviationEvent) => x.Timestamp > this.thirtyDaysAgo);
				let CMMSCaseHistoryThirtyDays: CaseFile[] = CMMSCaseHistory.filter((x: CaseFile) => x.OpenedAt > this.thirtyDaysAgo);

				this.assetHistoryThirtyDays = new AssetHistory(assetID, ruleEventsThirtyDays, sensorEventsThirtyDays, actionsThirtyDays, deviationEventsThirtyDays, CMMSCaseHistoryThirtyDays);
				this.assetHistory = new AssetHistory(assetID, ruleEvents, sensorEvents, this.ActionHistory, deviationEvents, CMMSCaseHistory);
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: this.constructor.combineLatest", false); }
		});
	}

	/**
	 * getActionTypeFromValue
	 * @param value value 
	 * @returns 
	 */
	getActionTypeFromValue(value: number): string {
		let result = this.actionTypes.find((x: IActionType) => x.ActionTypeID == value)?.Description;
		if (result) {
			return result;
		} else {
			return "";
		}
	}

	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}

	/**
	 * goToItem 
	 * @param type type  
	 * @param id id
	 */
	goToItem(type: number, id: number) {
		switch (type) {
			case 1: // rule event
				this.router.navigate([`./${this.accountGUID}/organisation/${this.organisationUID}/event/${id}`],
					{
						queryParams: {
							eventType: 2
						}
					}
				);
				break;
			case 2: // sensor event
				this.router.navigate([`./${this.accountGUID}/organisation/${this.organisationUID}/event/${id}`],
					{
						queryParams: {
							eventType: 1
						}
					}
				);
				break;
			case 3: // action
				this.router.navigate([`./${this.accountGUID}/organisation/${this.organisationUID}/actions/${id}`]);
				break;
			case 4: // deviation event
				this.router.navigate([`./${this.accountGUID}/organisation/${this.organisationUID}/event/${id}`],
					{
						queryParams: {
							eventType: 4
						}
					}
				);
				break;
			case 5: // CMMS Case
				this.router.navigate([`./${this.accountGUID}/organisation/${this.organisationUID}/SAPCases/${id}`],
					{
						queryParams: {}
					}
				);
				break;
			default:
				console.log("Invalid type");
				break;
		}
		this.dialogRef.close();
	}

	/**
	 * changeTab
	 * @param tab tab  
	 */
	changeTab(tab: number) {
		this.selectedTab = tab;
	}

	/**
	 * updateSelectedTab
	 * @param event event  
	 */
	updateSelectedTab(event: any) {
		this.selectedTab = event.selectedIndex;
	}

	/**
	 * getColumnDefinition
	 * @returns 
	 */
	getColumnDefinition(): string {
		let result: string = "";
		for (let i: number = 0; i < this.numberOfColumns; i++) {
			result += "1fr ";
		}
		result = result.trimEnd();
		return result;
	}

	/**
		 * checkFeature
		 * @param feature feature   
		 * @returns 
		 */
	checkFeature(feature: AccountFeatures): boolean {
		if (this.selectedAccount && this.selectedAccount.Features) {
			return AccountsService.checkFeature(feature, this.selectedAccount)
		}
		return false;
	}

	// /**
	//  * getModelTypes
	//  */
	// getModelTypes() {
	// 	// Create a local Subject to control the subscription lifecycle
	// 	const fetchDataSubject = new Subject<void>();

	// 	this.modelsService.getModelTypes$(this.accountGUID, this.organisationUID).pipe(
	// 		takeUntil(fetchDataSubject), distinctUntilChanged(),
	// 		filter((modelTypes) => !!modelTypes),
	// 		finalize(() => {
	// 			// Ensure the subscription is terminated and the subject is completed
	// 			fetchDataSubject.next();
	// 			fetchDataSubject.complete();
	// 			this.globalFunctions.displaySpinner(false)
	// 		})
	// 	).subscribe({
	// 		next: (modelTypes: any) => {
	// 			this.modelTypes = modelTypes;
	// 		},
	// 		error: (error) => {
	// 			if (error.status === 404) {

	// 			}
	// 		}
	// 	});
	// }

	/**
	 * getModelTypeName
	 * @param modelTypeID modelTypeID  
	 * @returns 
	 */
	getModelTypeName(modelTypeID: number): string {
		let result: string = "";
		let modelType = this.modelTypes.find((x: ModelType) => x.ModelTypeID == modelTypeID);
		if (modelType) {
			result = modelType.ModelName;
		}
		return result;
	}

	/**
	 * getBannerHeight
	 */
	getBannerHeight(): string {
		let multiplier = this.numberOfColumns > 3 ? "0.2" : "0.15";
		return `calc(var(--window-height) * ${multiplier})`;
	}

	filterActions(event: any) {

		if (event.value == 0) {
			this.assetHistory.ActionHistory = this.ActionHistory;
		} else {
			this.assetHistory.ActionHistory = this.ActionHistory.filter(a => a.ActionType == event.value);
		}

	}
}
